import { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle={
        submitted
          ? undefined
          : "Enter your account email and we'll send you instructions to reset your password."
      }
      footer={
        <Link
          to="/login"
          className="text-slate-900 font-medium hover:underline"
          data-testid="forgot-go-login-link"
        >
          Back to log in
        </Link>
      }
    >
      {submitted ? (
        <div
          className="rounded-2xl bg-emerald-50 border border-emerald-100 px-4 py-5 text-sm text-emerald-800"
          data-testid="forgot-success"
        >
          {"If an account exists for "}
          <span className="font-medium">{email}</span>
          {", you'll receive reset instructions shortly. Check your inbox (and spam folder)."}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700 mb-1.5"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              placeholder="you@example.com"
              data-testid="forgot-email-input"
            />
          </div>
          {error ? (
            <div
              className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
              data-testid="forgot-error"
            >
              {error}
            </div>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-3 text-base font-medium text-white shadow-md hover:bg-slate-800 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="forgot-submit-button"
          >
            {submitting ? "Sending…" : "Send reset instructions"}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
